import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateOrderFormField, setOrderFormError, setOrderFormLoading, clearOrderFormError, reverseGeocodeLocation } from '../../redux_files/actions/orderFormActions';
import { PANIPAT_BOUNDS } from '../../utils/locationConstants';
import { THEME } from '../../utils/theme';
import LocationModal from '../location_modal';

// Constants
const FREE_DELIVERY_RADIUS_KM = 7;
const DELIVERY_CENTER = { lat: 29.3857, lng: 76.9709 };

const DeliveryDetailsStep = () => {
  const dispatch = useDispatch();
  const [showLocationModal, setShowLocationModal] = useState(false);
  const formData = useSelector(state => state.orderForm);
  const {
    selectedDate,
    mealType,
    plateSize,
    numberOfPlates,
    name,
    phone,
    email,
    address,
    deliveryLocation,
    deliveryDistance,
    selectedDeliveryCoords,
    currentLocationLoading,
    errors
  } = formData;

  // Styles object
  const styles = {
    summaryContainer: {
      marginBottom: '20px'
    },
    summaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    summaryRowWithMargin: {
      marginTop: '8px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    deliveryChargeContainer: (deliveryDistance, numberOfPlates) => ({
      marginTop: '10px',
      padding: '10px',
      backgroundColor: deliveryDistance <= FREE_DELIVERY_RADIUS_KM ? THEME.info.light : 
                       (numberOfPlates >= 2 ? THEME.info.light : THEME.warning.light),
      borderLeft: `4px solid ${deliveryDistance <= FREE_DELIVERY_RADIUS_KM ? THEME.info.main : 
                              (numberOfPlates >= 2 ? THEME.info.main : THEME.warning.main)}`,
      borderRadius: '4px',
      color: deliveryDistance <= FREE_DELIVERY_RADIUS_KM ? THEME.info.dark : 
             (numberOfPlates >= 2 ? THEME.info.dark : THEME.warning.dark),
      fontSize: '13px',
      lineHeight: '1.5'
    }),
    chargeText: {
      marginTop: '5px'
    },
    tipText: {
      marginTop: '5px',
      fontStyle: 'italic',
      fontSize: '12px'
    },
    selectedLocationContainer: {
      marginTop: '10px',
      padding: '10px',
      backgroundColor: THEME.success.background,
      borderLeft: `4px solid ${THEME.success.main}`,
      borderRadius: '4px',
      color: THEME.success.dark,
      fontSize: '13px'
    },
    currentLocationButton: {
      width: '100%',
      marginTop: '10px',
      textAlign: 'center'
    },
    errorContainer: (isPermissionError) => ({
      marginTop: '10px',
      padding: '12px',
      backgroundColor: isPermissionError ? THEME.error.light : THEME.warning.light,
      borderLeft: `4px solid ${isPermissionError ? THEME.error.main : THEME.warning.main}`,
      borderRadius: '4px',
      color: isPermissionError ? THEME.error.darkText : THEME.warning.dark,
      fontSize: '13px',
      lineHeight: '1.5'
    }),
    errorTitle: {
      marginTop: '8px'
    },
    fixInstructions: {
      marginTop: '10px',
      fontSize: '12px',
      fontStyle: 'italic'
    }
  };

  // Calculate distance using Haversine formula
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Calculate pricing
  const calculatePrice = () => {
    const plates = numberOfPlates === '' ? 0 : numberOfPlates;
    let platePrice = 0;
    let deliveryCharge = 0;

    if (plateSize === 'half') {
      platePrice = 50;
    } else {
      if (plates > 15) platePrice = 55;
      else if (plates > 6) platePrice = 60;
      else platePrice = 70;
    }

    if (deliveryDistance !== null) {
      if (deliveryDistance <= FREE_DELIVERY_RADIUS_KM) {
        deliveryCharge = 0;
      } else if (plates >= 2) {
        deliveryCharge = 0;
      } else if (plates === 1) {
        const chargeableDistance = deliveryDistance - FREE_DELIVERY_RADIUS_KM;
        deliveryCharge = Math.ceil(chargeableDistance) * 5;
      }
    }

    const subtotal = platePrice * plates;
    const total = subtotal + deliveryCharge;

    return { platePrice, subtotal, deliveryCharge, total, distance: deliveryDistance };
  };

  const pricing = calculatePrice();

  const isWithinPanipat = (location) => {
    return (
      location.lat >= PANIPAT_BOUNDS.minLat &&
      location.lat <= PANIPAT_BOUNDS.maxLat &&
      location.lng >= PANIPAT_BOUNDS.minLng &&
      location.lng <= PANIPAT_BOUNDS.maxLng
    );
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      dispatch(setOrderFormError('currentLocation', 'Geolocation is not supported by your browser.'));
      return;
    }

    dispatch(setOrderFormLoading(true));
    dispatch(clearOrderFormError('currentLocation'));

    navigator.geolocation.getCurrentPosition((position) => {
      const loc = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };

      if (!isWithinPanipat(loc)) {
        dispatch(setOrderFormLoading(false));
        dispatch(setOrderFormError('currentLocation', 'Your current location is outside the Panipat delivery area.'));
        return;
      }

      const distance = calculateDistance(DELIVERY_CENTER.lat, DELIVERY_CENTER.lng, loc.lat, loc.lng);
      
      // Dispatch saga action to reverse geocode
      dispatch(reverseGeocodeLocation(loc.lat, loc.lng));
      
      // Update coordinates and distance
      dispatch(updateOrderFormField('selectedDeliveryCoords', loc));
      dispatch(updateOrderFormField('deliveryDistance', distance));
      dispatch(setOrderFormLoading(false));
    }, (error) => {
      dispatch(setOrderFormLoading(false));
      
      if (error.code === error.PERMISSION_DENIED) {
        dispatch(setOrderFormError('currentLocation', 'Location permission denied. Please enable location access in your browser settings and try again.'));
      } else if (error.code === error.POSITION_UNAVAILABLE) {
        dispatch(setOrderFormError('currentLocation', 'Location information is unavailable. Please try again or use manual location selection.'));
      } else if (error.code === error.TIMEOUT) {
        dispatch(setOrderFormError('currentLocation', 'Location request timed out. Please try again.'));
      } else {
        dispatch(setOrderFormError('currentLocation', 'Unable to fetch your current location. Please try again.'));
      }
    });
  };
  const handleLocationSelect = (location, address) => {
    const distance = calculateDistance(DELIVERY_CENTER.lat, DELIVERY_CENTER.lng, location.lat, location.lng);
    dispatch(updateOrderFormField('deliveryLocation', address));
    dispatch(updateOrderFormField('selectedDeliveryCoords', location));
    dispatch(updateOrderFormField('deliveryDistance', distance));
    setShowLocationModal(false);
  };  
  return (
    <>
      {/* Order Summary from Previous Steps */}
      <div className='selected-date-display' style={styles.summaryContainer}>
        <div style={styles.summaryRow}>
          <span>Delivery Date:</span>
          <strong>{new Date(selectedDate).toLocaleDateString('en-IN', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</strong>
        </div>
        <div style={styles.summaryRowWithMargin}>
          <span>Meal Type:</span>
          <strong>{mealType ? mealType.charAt(0).toUpperCase() + mealType.slice(1) : 'N/A'}</strong>
        </div>
        <div style={styles.summaryRowWithMargin}>
          <span>Plate Type:</span>
          <strong>{plateSize === 'full' ? 'Full Plate' : 'Buffet'}</strong>
        </div>
        <div style={styles.summaryRowWithMargin}>
          <span>Quantity:</span>
          <strong>{numberOfPlates} {numberOfPlates === 1 ? 'plate' : 'plates'}</strong>
        </div>
      </div>
      
      <div>
        <div className='form-group'>
          <label>Name <span className='required'>*</span></label>
          <div className='form-input-wrapper'>
            <input
              type='text'
              value={name}
              onChange={(e) => dispatch(updateOrderFormField('name', e.target.value))}
              placeholder='Enter your name'
              className='form-input'
            />
            {errors.name && (
              <div className='error-message'>{errors.name}</div>
            )}
          </div>
        </div>

        <div className='form-group'>
          <label>Phone<span className='required'>*</span></label>
          <div className='form-input-wrapper'>
            <input
              type='tel'
              inputMode='numeric'
              value={phone}
              onChange={(e) => {
                const value = e.target.value;
                dispatch(updateOrderFormField('phone', value));
                
                // Real-time validation
                if (value.trim() === '') {
                  dispatch(clearOrderFormError('phone'));
                } else if (!/^[6-9]\d{9}$/.test(value.trim())) {
                  dispatch(setOrderFormError('phone', 'Please enter a valid 10-digit phone number'));
                } else {
                  dispatch(clearOrderFormError('phone'));
                }
              }}
              placeholder='Enter your phone number'
              className='form-input'
            />
            {errors.phone && (
              <div className='error-message'>{errors.phone}</div>
            )}
          </div>
        </div>

        <div className='form-group'>
          <label>Address <span className='required'>*</span></label>
          <div className='form-input-wrapper'>
            <textarea
              value={address}
              onChange={(e) => dispatch(updateOrderFormField('address', e.target.value))}
              placeholder='Enter your delivery address'
              className='form-input'
              rows='3'
            />
            {errors.address && (
              <div className='error-message'>{errors.address}</div>
            )}
          </div>
        </div>

        <div className='form-group'>
          <label>Email</label>
          <div className='form-input-wrapper'>
            <input
              type='email'
              inputMode='email'
              value={email}
              onChange={(e) => {
                const value = e.target.value;
                dispatch(updateOrderFormField('email', value));
                
                // Real-time validation
                if (value.trim() === '') {
                  dispatch(clearOrderFormError('email'));
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
                  dispatch(setOrderFormError('email', 'Please enter a valid email address'));
                } else {
                  dispatch(clearOrderFormError('email'));
                }
              }}
              placeholder='Enter your email (optional)'
              className='form-input'
            />
            {errors.email && (
              <div className='error-message'>{errors.email}</div>
            )}
          </div>
        </div>

        <div className='form-group'>
          <label>Delivery Location <span className='required'>*</span></label>
          <div className='form-input-wrapper'>
            <button
              type='button'
              className='nav-button prev-button'
              style={styles.currentLocationButton}
              onClick={handleUseCurrentLocation}
              disabled={currentLocationLoading}
            >
              {currentLocationLoading ? 'Detecting Current Location...' : 'Use My Current Location'}
            </button>
            <button
              type='button'
              className='nav-button next-button'
              style={styles.currentLocationButton}
              onClick={() => setShowLocationModal(true)}
            >
              Select Custom Location
            </button>
          </div>
        </div>
        
        {/* Delivery Charge Information */}
        {deliveryLocation && deliveryDistance !== null && (
          <div style={styles.deliveryChargeContainer(deliveryDistance, numberOfPlates)}>
            {deliveryDistance <= FREE_DELIVERY_RADIUS_KM ? (
              <div style={styles.chargeText}>
                <strong>🎉 Free Delivery!</strong> You are within {FREE_DELIVERY_RADIUS_KM}km radius.
              </div>
            ) : numberOfPlates >= 2 ? (
              <div style={styles.chargeText}>
                <strong>🎉 Free Delivery!</strong> Free delivery for 2 or more plates.
              </div>
            ) : numberOfPlates === 1 ? (
              <div style={styles.chargeText}>
                <strong>💰 Delivery Charge:</strong> ₹{pricing.deliveryCharge} 
                <div style={styles.tipText}>
                  💡 Tip: Order 2+ plates for free delivery!
                </div>
              </div>
            ) : (
              <div style={styles.chargeText}>
                <strong>ℹ️</strong> Select number of plates to see delivery charge.
              </div>
            )}
          </div>
        )}
        {deliveryLocation && (
          <div style={styles.selectedLocationContainer}>
            <strong>✓ Selected Location:</strong> {deliveryLocation}
          </div>
        )}
        {errors.currentLocation && (
          <div style={styles.errorContainer(errors.currentLocation.includes('permission denied'))}>
            <strong>{errors.currentLocation.includes('permission denied') ? '⚠️ Permission Denied' : '⚠️ Error'}</strong>
            <div style={styles.errorTitle}>{errors.currentLocation}</div>
            {errors.currentLocation.includes('permission denied') && (
              <div style={styles.fixInstructions}>
                📱 <strong>How to fix:</strong> Open your browser settings, find location permissions for this site, and select "Allow". Then click "Use My Current Location" again.
              </div>
            )}
          </div>
        )}
        
        {errors.deliveryLocation && (
          <div className='error-message'>{errors.deliveryLocation}</div>
        )}
      </div>

      <LocationModal
        showModal={showLocationModal}
        selectedDeliveryCoords={selectedDeliveryCoords}
        onLocationSelect={handleLocationSelect}
        onClose={() => setShowLocationModal(false)}
      />
    </>
  );
};

export default DeliveryDetailsStep;
